// country codes from Geo.pm
export const US = 'US';
export const CA = 'CA';
export const OO = 'OO';

// country code to name from Geo.pm
export const COUNTRY_CODE_MAP = {
  [US]: 'United States of America',
  [CA]: 'Canada',
  [OO]: 'Other',
};

// form names and display names
export const COUNTRY_NAME = 'country';
export const COUNTRY_DISPLAY = 'Country';
// have to use something that chrome autofill and others don't recognize as zipcode
export const ZIPCODE_NAME = 'pc';
export const SALARY_NAME = 'desired_salary';
export const SALARY_DISPLAY = 'Minimum Desired Pay (optional)';
export const REMOTE_NAME = 'will_work_remotely';
export const REMOTE_DISPLAY = 'I am willing to work remotely';
export const LOCATION_NAME = 'location';
// rdb_opt_in is an optional field to this form since ticket JS-9609
export const RDB_OPT_IN_NAME = 'rdb_opt_in';
export const RDB_DISPLAY = 'Public: Make my profile searchable by employers';

/**
 * Craft geoac autocomplete url for zip code autocomplete
 * @param {string} autocompleteUrl
 * @param {string} countryCode
 */
export const getZipcodeAutocompleteUrlByCountry = (autocompleteUrl, countryCode) => {
  const [path, queryString] = autocompleteUrl.split('?');
  // autocompleteUrl has some default query params such as lat, lng, filter
  // of the default params, we only want to override filter with the selected country
  const queryParams = new URLSearchParams(queryString);
  // location is the search query param
  // __term__ is replaced by Autocomplete
  queryParams.set('location', '__term__');
  // select_code forces autocomplete suggestions described in https://wiki.zr.org/geoac
  queryParams.set('select_code', 'p');
  // map Geo.pm country codes to https://wiki.zr.org/geoac filter options
  // this overrides the default filter if set by build_location_autocomplete
  switch (countryCode) {
    case US: queryParams.set('filter', 'usa'); break;
    case CA: queryParams.set('filter', 'ca'); break;
    // this form should never get here, since the zipcode field hides when no country is selected
    default:
  }
  return `${path}?${queryParams}`;
};

/**
 * This is run as we are processing autocomplete item results
 * If it's not what we expect (an array), have the caller handle an error
 * @param {object} json
 */
export const parseSuggestions = (json) => {
  if (Array.isArray(json.results)) {
    return json.results;
  }
  // throw an Error, have a message if we decide to show it
  throw new Error('Something went wrong on our end, please try again later.');
};

/**
 * Display zipcode in front, since the job seeker searching locations by zipcode
 * @param {string} zipcode
 * @param {string} city
 * @param {string} state
 */
export const getZipcodeInputDisplay = (zipcode, city, state) => (
  `${zipcode} ${city}, ${state}`
);

export const getZipcodeDisplay = (country) => (
  country === US ? 'ZIP Code' : 'Postal Code'
);

/**
 * Parse autocomplete suggestion into display suggestion text
 * Also move the zip code (their text match) to the front
 * @param {object} item
 */
export const parseSuggestionItem = (item) => {
  if (!item) {
    return '';
  }
  const { postal_code: zipcode, city, admin1_code: state } = item;
  return getZipcodeInputDisplay(zipcode, city, state);
};

export const isUSorCA = (countryCode) => (
  countryCode === US || countryCode === CA
);

/**
 * Very specific function that styles the item hint message as an error
 * After we search geoc for an input longer than 1 or 3, then signal to the user that their input is probably invalid
 * @param {string} countryCode
 */
export const showItemErrorClass = (countryCode) => (inputValue) => {
  switch (countryCode) {
    case US:
      return inputValue.length > 3;
    case CA:
      return inputValue.length > 1;
    default:
      return false;
  }
};

/**
 * Given known information, setup form values
 *
 * @param {object} initialValues
 */
export const calculateFormInitialValues = ({
  country, city, state, location, zipcode, desiredSalary, willWorkRemotely, rdbOptIn,
}) => {
  const initialValues = {};
  // if we actually have every location field, then use that
  if (country && isUSorCA(country) && city && state && zipcode && location) {
    initialValues[COUNTRY_NAME] = country;
    initialValues[ZIPCODE_NAME] = getZipcodeInputDisplay(zipcode, city, state);
    // make the initial location object mirror the geoac schema
    // so it looks the same when the form value is submitted
    initialValues[LOCATION_NAME] = {
      cc: country,
      city,
      admin1_code: state,
      postal_code: zipcode,
      // for Yup validation
      name: location,
    };
  } else {
    // we might have the country from their ip address, or we just don't have the rest of their location
    initialValues[COUNTRY_NAME] = country || '';
    // even if they have a zipcode, force them to select a location with all fields
    initialValues[ZIPCODE_NAME] = '';
  }
  initialValues[SALARY_NAME] = desiredSalary || '';
  initialValues[REMOTE_NAME] = willWorkRemotely || false;
  initialValues[RDB_OPT_IN_NAME] = rdbOptIn;

  return initialValues;
};
